import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Download,
  Maximize2,
  X,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Alert, AlertDescription, Button } from '@roger/ui-components';
import { client, downloadBillingCycleUb04Pdf } from '../../../services/api';
import { UB04_PDF_SERVICE_LINE_ROWS } from './types';

// The paper UB-04 holds 22 rows, so the PDF drops later lines.
export function PdfServiceLineNotice({
  lineCount,
  className = '',
}: {
  lineCount: number;
  className?: string;
}) {
  if (lineCount <= UB04_PDF_SERVICE_LINE_ROWS) return null;
  return (
    <Alert
      className={`border-amber-200 bg-amber-50 ${className}`}
      data-testid='pdf-service-line-notice'
    >
      <AlertCircle className='h-4 w-4 text-amber-600' />
      <AlertDescription className='text-amber-800'>
        The PDF preview shows only the first {UB04_PDF_SERVICE_LINE_ROWS} of{' '}
        {lineCount} service lines. The electronic claim includes all {lineCount}{' '}
        lines.
      </AlertDescription>
    </Alert>
  );
}

export function SavedUb04Preview({
  billingCycleId,
  reloadKey,
  stale,
  serviceLineCount = 0,
  disabled = false,
  onPreviewModeChange,
}: {
  billingCycleId: string;
  reloadKey: number;
  stale: boolean;
  // Line count of the last saved worksheet, which is what the PDF renders.
  serviceLineCount?: number;
  disabled?: boolean;
  onPreviewModeChange?: (open: boolean) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const [previewModeOpen, setPreviewModeOpen] = useState(false);

  useEffect(() => {
    let revoked = false;
    let nextUrl: string | null = null;

    const load = async () => {
      if (disabled) return;
      setIsLoading(true);
      setError(null);
      try {
        const resp = await downloadBillingCycleUb04Pdf({
          path: { billing_cycle_id: billingCycleId },
          client,
          responseType: 'blob',
        });
        if (resp.error || !resp.data) {
          throw resp.error ?? new Error('Empty UB-04 PDF response');
        }
        nextUrl = URL.createObjectURL(resp.data as Blob);
        if (!revoked) {
          setUrl(previous => {
            if (previous) URL.revokeObjectURL(previous);
            return nextUrl;
          });
        }
      } catch (err) {
        console.error('Failed to load saved UB-04 preview:', err);
        setError('Unable to load saved preview.');
      } finally {
        if (!revoked) setIsLoading(false);
      }
    };

    void load();
    return () => {
      revoked = true;
      if (nextUrl) URL.revokeObjectURL(nextUrl);
    };
  }, [billingCycleId, disabled, reloadKey, retryKey]);

  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url]
  );

  useEffect(() => {
    onPreviewModeChange?.(previewModeOpen);
    return () => onPreviewModeChange?.(false);
  }, [onPreviewModeChange, previewModeOpen]);

  useEffect(() => {
    if (!previewModeOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setPreviewModeOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [previewModeOpen]);

  return (
    <aside className='space-y-3 rounded border bg-background p-3'>
      <div className='flex items-center justify-between gap-2'>
        <div>
          <h3 className='text-sm font-semibold'>Saved UB-04</h3>
          <p className='text-xs text-muted-foreground'>
            Preview shows last saved worksheet
          </p>
        </div>
        {stale ? (
          <span className='rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800'>
            Unsaved changes
          </span>
        ) : null}
      </div>

      <PdfServiceLineNotice lineCount={serviceLineCount} />

      {isLoading && !url ? (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' /> Loading preview
        </div>
      ) : null}

      {error ? (
        <Alert className='border-amber-200 bg-amber-50'>
          <AlertCircle className='h-4 w-4 text-amber-600' />
          <AlertDescription className='text-amber-800'>
            <div className='flex items-center justify-between gap-2'>
              <span>{error}</span>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => setRetryKey(key => key + 1)}
              >
                <RefreshCw className='mr-1 h-3 w-3' /> Retry
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {url ? (
        <div className='space-y-2'>
          <iframe
            title='Saved UB-04 preview'
            src={url}
            className='h-[32rem] w-full touch-pan-y overscroll-x-contain rounded border'
          />
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setPreviewModeOpen(true)}
            >
              <Maximize2 className='mr-1 h-3 w-3' /> PDF preview mode
            </Button>
            <Button type='button' variant='outline' size='sm' asChild>
              <a href={url} download='ub04.pdf'>
                <Download className='mr-1 h-3 w-3' /> Download
              </a>
            </Button>
          </div>
        </div>
      ) : null}

      {previewModeOpen ? (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='saved-ub04-pdf-preview-title'
          className='fixed inset-0 z-50 flex bg-background'
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              setPreviewModeOpen(false);
            }
          }}
        >
          <div className='flex min-h-0 w-full flex-col'>
            <div className='flex items-start justify-between gap-3 border-b px-4 py-3'>
              <div>
                <h2
                  id='saved-ub04-pdf-preview-title'
                  className='text-lg font-semibold'
                >
                  Saved UB-04 PDF preview
                </h2>
                <p className='text-sm text-muted-foreground'>
                  Full-modal preview of the last saved worksheet PDF. Press
                  Escape, Cancel, or X to return to the worksheet.
                </p>
              </div>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                aria-label='Close PDF preview'
                onClick={() => setPreviewModeOpen(false)}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
            <PdfServiceLineNotice
              lineCount={serviceLineCount}
              className='mx-4 my-2 w-auto'
            />
            {url ? (
              <iframe
                title='Full-size saved UB-04 preview'
                src={url}
                className='min-h-0 flex-1 touch-pan-y overscroll-x-contain border-0'
              />
            ) : null}
            <div className='flex justify-end border-t px-4 py-3'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setPreviewModeOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
